import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ecoLogo from "../../../img/eco/logo-white.png";
import { clearEmployeeSession } from "../../../store/employeeAuthSlice";
import type { RootState } from "../../../store/store";
import { changeFirstLoginPasswordApi } from "../api/employeeAuthApi";
import PasswordRules, { isStrongPassword } from "../components/PasswordRules";
import { COLABORADOR_LOGIN } from "../routes";

import "./Colaborador.css";

/**
 * Primeiro acesso: troca obrigatória da senha provisória entregue pelo DP.
 *
 * Ao concluir, a sessão é LIMPA e o colaborador volta ao login. Não é atrito
 * gratuito: a troca carimba `passwordChangedAt` no backend, e o token usado
 * nesta própria tela deixa de valer no mesmo instante. Reaproveitá-lo daria uma
 * tela de erro logo depois.
 */
export default function FirstAccessPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token = useSelector((s: RootState) => s.employeeAuth?.accessToken);

  const [password, setPassword] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const confere = confirmacao.length > 0 && password === confirmacao;
  const podeEnviar = useMemo(
    () => isStrongPassword(password) && confere && !loading,
    [password, confere, loading]
  );

  const definir = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!podeEnviar || !token) return;

    setErro(null);
    setLoading(true);

    try {
      await changeFirstLoginPasswordApi(token, password, confirmacao);

      dispatch(clearEmployeeSession());
      toast.success("Senha definida. Entre novamente com a senha nova.");
      navigate(COLABORADOR_LOGIN(), { replace: true });
    } catch (error: any) {
      const mensagem = error?.message || "Não foi possível definir a senha.";
      setErro(mensagem);
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="colabPage colabPage--auth">
      <img className="colabLogo" src={ecoLogo} alt="ECO System Serviços" />

      <form className="colabCard" onSubmit={definir}>
        <h1 className="colabCard__title">Crie a sua senha</h1>
        <p className="colabCard__subtitle">
          A senha que o Departamento Pessoal entregou é provisória. Escolha uma senha só sua
          para continuar.
        </p>

        {erro ? <p className="colabError">{erro}</p> : null}

        <div className="colabField">
          <label htmlFor="colab-nova">Nova senha</label>
          <input
            id="colab-nova"
            className="colabInput"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
        </div>

        <PasswordRules value={password} />

        <div className="colabField">
          <label htmlFor="colab-confirma">Repita a nova senha</label>
          <input
            id="colab-confirma"
            className="colabInput"
            type="password"
            autoComplete="new-password"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            disabled={loading}
          />
          {confirmacao.length > 0 && !confere ? (
            <span className="colabHint" style={{ color: "var(--eco-danger-ink)" }}>
              As senhas não são iguais.
            </span>
          ) : null}
        </div>

        <button type="submit" className="colabBtn colabBtn--primary" disabled={!podeEnviar}>
          {loading ? "Salvando..." : "Salvar e entrar de novo"}
        </button>
      </form>
    </div>
  );
}
